import { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { useBooking } from '../context/BookingContext';
import { useNavigate } from 'react-router-dom';

const Calendar = () => {
  const { courts, bookings } = useBooking();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getAvailableSlotsCount = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayBookings = bookings.filter(b => b.date === dateStr && b.status === 'confirmed');
    
    const timeSlots: string[] = [];
    for (let h = 7; h <= 21; h++) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    }
    const totalSlots = courts.length * timeSlots.length;
    
    let occupiedSlots = 0;
    dayBookings.forEach(booking => {
      const startIdx = timeSlots.indexOf(booking.startTime);
      const endIdx = timeSlots.indexOf(booking.endTime);
      if (startIdx !== -1 && endIdx !== -1) {
        occupiedSlots += (endIdx - startIdx);
      }
    });
    
    return totalSlots - occupiedSlots;
  };

  const handleDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    navigate('/book', { state: { selectedDate: dateStr } });
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  return (
    <div className="max-w-lg mx-auto p-4">
      <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <button 
            onClick={goToPreviousMonth} 
            className="text-green-700 hover:text-green-900 font-bold text-xl hover:bg-green-50 p-2 rounded-full transition touch-target"
          >
            ←
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-green-800">{format(currentMonth, 'MMMM yyyy')}</h2>
          <button 
            onClick={goToNextMonth} 
            className="text-green-700 hover:text-green-900 font-bold text-xl hover:bg-green-50 p-2 rounded-full transition touch-target"
          >
            →
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="font-medium text-green-700 text-xs sm:text-sm py-1 sm:py-2">{day}</div>
          ))}
          {daysInMonth.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isToday_ = isToday(day);
            const availableSlots = getAvailableSlotsCount(day);
            const isPast = day < new Date() && !isToday_;
            const isClickable = !isPast && availableSlots > 0;
            
            return (
              <div
                key={day.toISOString()}
                className={`p-1 sm:p-2 rounded-xl transition cursor-default
                  ${!isSameMonth(day, currentMonth) ? 'text-gray-300' : ''}
                  ${isToday_ ? 'bg-green-600 text-white font-bold shadow-lg' : ''}
                  ${!isPast && availableSlots > 0 && !isToday_ ? 'bg-green-100 hover:bg-green-200 cursor-pointer hover:scale-105' : ''}
                  ${!isPast && availableSlots === 0 && !isToday_ ? 'bg-red-50 text-gray-400' : ''}
                  ${isPast && !isToday_ ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
                `}
                onClick={() => isClickable && handleDateClick(day)}
              >
                <div className="text-base sm:text-lg font-semibold">{format(day, 'd')}</div>
                {!isPast && !isToday_ && (
                  <div className={`text-[10px] sm:text-xs mt-0.5 font-medium ${availableSlots > 0 ? 'text-green-600' : 'text-red-400'}`}>
                    {availableSlots > 0 ? `${availableSlots} avail` : 'Full'}
                  </div>
                )}
                {isToday_ && (
                  <div className="text-[10px] sm:text-xs mt-0.5 text-green-100 font-medium">Today</div>
                )}
                {isPast && !isToday_ && (
                  <div className="text-[10px] sm:text-xs mt-0.5 text-gray-400">Past</div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
          <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-green-100 rounded"></span>
            <span>Available</span>
          </span>
          <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-green-600 rounded"></span>
            <span>Today</span>
          </span>
          <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-red-50 border border-red-200 rounded"></span>
            <span>Full</span>
          </span>
          <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-gray-100 rounded"></span>
            <span>Past</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default Calendar;